import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertCircle,
  Briefcase,
  CheckCircle2,
  ChevronRight,
  Clock,
  DollarSign,
  Lightbulb,
  MapPin,
  Sparkles,
  Timer,
  Zap,
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { useMatchFeedPreviewCopy } from './useMatchFeedPreviewCopy';
import type { MatchFeedPreviewCopy } from './matchFeedPreviewCopy';
import { useHeroPreviewCopy } from './useHeroPreviewCopy';
import { useFeedPreviewJobs, type FeedJobFixture } from './usePreviewJobFixtures';

type Phase = 'pipeline' | 'feed' | 'cursor-select' | 'explain';

type MatchBand = 'strong_fit' | 'good_fit' | 'partial_fit';

interface ScoreBreakdown {
  skill_overlap: number;
  semantic_similarity: number;
  experience_alignment: number;
  budget_fit: number;
  location_fit: number;
  deadline_feasibility: number;
}

interface FeedJob extends FeedJobFixture {}

const BAND_LABEL = (copy: MatchFeedPreviewCopy): Record<MatchBand, { label: string; className: string }> => ({
  strong_fit: { label: copy.bandStrongFit, className: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300' },
  good_fit: { label: copy.bandGoodFit, className: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-300' },
  partial_fit: { label: copy.bandPartialFit, className: 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300' },
});

const scoreColor = (s: number) =>
  s >= 90 ? '#10b981' : s >= 80 ? '#6366f1' : s >= 70 ? '#f59e0b' : '#94a3b8';

function PreviewMatchRing({
  score,
  ringId,
  size = 'sm',
  highlight = false,
  matchLabel = 'Match',
}: {
  score: number;
  ringId: string;
  size?: 'sm' | 'md';
  highlight?: boolean;
  matchLabel?: string;
}) {
  const dim = size === 'md' ? 44 : 36;
  const radius = size === 'md' ? 17 : 14;
  const center = dim / 2;
  const circumference = 2 * Math.PI * radius;
  const dash = (Math.max(0, Math.min(100, score)) / 100) * circumference;
  const gradientId = `amf-ring-${ringId.replace(/[^a-zA-Z0-9_-]/g, '')}`;

  return (
    <div
      className={`home-hero-preview-match-ring relative shrink-0 ${size === 'md' ? 'h-11 w-11' : 'h-9 w-9'} ${highlight ? 'hfs-agent-preview__ring--highlight' : ''}`}
      aria-label={`${score} percent match`}
    >
      <svg width={dim} height={dim} viewBox={`0 0 ${dim} ${dim}`} className="block" aria-hidden>
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#22d3ee" />
            <stop offset="50%" stopColor="#6C47FF" />
            <stop offset="100%" stopColor="#7c3aed" />
          </linearGradient>
        </defs>
        <circle cx={center} cy={center} r={radius} fill="none" stroke="rgba(148, 163, 184, 0.22)" strokeWidth="2.5" />
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={`url(#${gradientId})`}
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeDasharray={`${dash} ${circumference}`}
          transform={`rotate(-90 ${center} ${center})`}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
        <span className={`font-bold uppercase tracking-wide text-slate-400 ${size === 'md' ? 'text-[5px]' : 'text-[4px]'}`}>
          {matchLabel}
        </span>
        <span className={`font-black text-gray-950 dark:text-white ${size === 'md' ? 'text-[9px]' : 'text-[8px]'}`}>
          {score}%
        </span>
      </div>
    </div>
  );
}

function FeedJobCard({
  job,
  selected,
  highlightRing,
  compact,
  cardRef,
  copy,
}: {
  job: FeedJob;
  selected?: boolean;
  highlightRing?: boolean;
  compact?: boolean;
  cardRef?: React.Ref<HTMLButtonElement>;
  copy: MatchFeedPreviewCopy;
}) {
  const heroCopy = useHeroPreviewCopy();
  const initial = job.company.charAt(0).toUpperCase();
  const visibleSkills = job.skills.slice(0, compact ? 2 : 3);
  const extraSkills = Math.max(0, job.skills.length - visibleSkills.length);
  const band = BAND_LABEL(copy)[job.band];

  if (compact) {
    return (
      <button
        ref={cardRef}
        type="button"
        className={`flex w-full items-center gap-2 rounded-xl border p-2 text-left transition-colors ${
          selected
            ? 'border-indigo-200 bg-indigo-50/60 dark:border-indigo-800/60 dark:bg-indigo-950/20'
            : 'border-gray-100 bg-white dark:border-gray-800'
        }`}
      >
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-[10px] font-bold text-indigo-600 dark:bg-indigo-950/40 dark:text-indigo-300">
          {initial}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[9px] font-bold text-gray-900 dark:text-white">{job.title}</p>
          <p className="truncate text-[8px] text-gray-500">{job.company} · {job.location}</p>
          <span className={`mt-0.5 inline-flex rounded-full px-1.5 py-0.5 text-[6.5px] font-semibold ${band.className}`}>
            {band.label}
          </span>
        </div>
        <PreviewMatchRing score={job.score} ringId={job.id} highlight={highlightRing} matchLabel={copy.matchLabel} />
      </button>
    );
  }

  return (
    <button
      ref={cardRef}
      type="button"
      className={`home-hero-preview-job-card home-hero-preview-job-card--portrait home-hero-preview-job-card--with-ring flex h-full w-full min-h-0 flex-col text-left ${
        selected ? 'home-hero-preview-job-card--selected' : ''
      }`}
    >
      <div className="absolute right-1 top-1 z-[1]">
        <PreviewMatchRing score={job.score} ringId={job.id} highlight={highlightRing} matchLabel={copy.matchLabel} />
      </div>
      <div className="relative shrink-0">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-50 text-[11px] font-bold text-indigo-600 dark:bg-indigo-950/40 dark:text-indigo-300">
          {initial}
        </div>
      </div>
      <div className="mt-1.5 min-w-0 shrink-0 pr-9">
        <p className="line-clamp-2 text-[11px] font-bold leading-snug text-gray-950 dark:text-white">{job.title}</p>
        <p className="mt-0.5 line-clamp-1 text-[10px] font-medium text-slate-600 dark:text-gray-400">{job.company}</p>
        <p className="mt-0.5 text-[9px] leading-snug text-slate-400">
          {job.source} · {job.postedLabel}
        </p>
      </div>
      <div className="home-hero-preview-job-card__tags mt-1.5 flex min-w-0 flex-col gap-0.5">
        {visibleSkills.map((skill) => (
          <span key={skill} className="home-hero-preview-job-card__tag w-full truncate">
            {skill}
          </span>
        ))}
        {extraSkills > 0 ? (
          <span className="home-hero-preview-job-card__tag home-hero-preview-job-card__tag--more w-fit">
            {heroCopy.jobsExtraSkills.replace('{{count}}', String(extraSkills))}
          </span>
        ) : null}
      </div>
      <span className={`mt-1 inline-flex w-fit rounded-full px-1.5 py-0.5 text-[7px] font-semibold ${band.className}`}>
        {band.label}
      </span>
      <p className="mt-1 shrink-0 text-[9px] leading-snug text-slate-400">{job.location}</p>
      <div className="home-hero-preview-job-card__footer mt-auto shrink-0">
        <p className="truncate text-[9px] text-slate-400">
          {job.experienceLevel} · {job.salary}
        </p>
        <span className="home-hero-preview-job-card__arrow" aria-hidden>
          <ChevronRight className="h-3.5 w-3.5" strokeWidth={2.25} />
        </span>
      </div>
    </button>
  );
}

function PreviewCursor({
  x,
  y,
  clicking,
  visible,
}: {
  x: number;
  y: number;
  clicking: boolean;
  visible: boolean;
}) {
  if (!visible) return null;

  return (
    <motion.div
      className="home-hero-preview-cursor pointer-events-none absolute z-[80]"
      initial={false}
      animate={{ left: x, top: y, scale: clicking ? 0.84 : 1, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 280, damping: 24, mass: 0.55 }}
    >
      <svg width="16" height="20" viewBox="0 0 18 22" fill="none" aria-hidden className="drop-shadow-sm">
        <path d="M1 1L1 16L5 12L8 20L11 19L8 11L14 10L1 1Z" fill="#0f172a" stroke="#ffffff" strokeWidth="1.25" />
      </svg>
      {clicking ? <span className="home-hero-preview-cursor-ripple" aria-hidden /> : null}
    </motion.div>
  );
}

function ExplainPanel({ job, detailStep, copy }: { job: FeedJob; detailStep: number; copy: MatchFeedPreviewCopy }) {
  const band = BAND_LABEL(copy)[job.band];
  const dimEntries = Object.entries(job.dims) as [keyof ScoreBreakdown, number][];

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="shrink-0 border-b border-gray-100 px-3 py-2 dark:border-gray-800">
        <p className="truncate text-[10px] font-bold text-gray-900 dark:text-white">{job.title}</p>
        <p className="text-[8px] text-gray-500">{job.company} · {job.source}</p>
      </div>

      <div className="min-h-0 flex-1 space-y-2 overflow-hidden px-3 py-2">
        {/* Score + band */}
        <div className="flex items-center gap-2 rounded-lg border border-emerald-100 bg-emerald-50/60 p-2 dark:border-emerald-900/30 dark:bg-emerald-950/20">
          <PreviewMatchRing score={job.score} ringId={`explain-${job.id}`} size="md" highlight matchLabel={copy.matchLabel} />
          <div className="min-w-0">
            <span className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[7px] font-bold ${band.className}`}>
              <CheckCircle2 className="h-2.5 w-2.5" />
              {band.label}
            </span>
            <p className="mt-1 text-[7px] text-gray-500 dark:text-gray-400">
              {copy.basedOnSkills}
            </p>
          </div>
        </div>

        {/* Meta pills */}
        {detailStep >= 1 && (
          <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-2 gap-1">
            {[
              { icon: MapPin, label: job.location },
              { icon: Briefcase, label: job.jobType },
              { icon: DollarSign, label: job.salary },
              { icon: Timer, label: job.experienceLevel },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="hfs-agent-preview__meta-pill">
                <Icon className="h-2.5 w-2.5 shrink-0 text-gray-400" />
                <span className="truncate">{label}</span>
              </div>
            ))}
          </motion.div>
        )}

        {/* Why matched */}
        {detailStep >= 2 && (
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-lg border border-indigo-100/60 bg-indigo-50/40 p-2 dark:border-indigo-900/40 dark:bg-indigo-950/20"
          >
            <p className="mb-1 flex items-center gap-1 text-[8px] font-bold text-indigo-800 dark:text-indigo-300">
              <Lightbulb className="h-3 w-3" />
              {copy.whyYouMatch}
            </p>
            <p className="text-[8px] leading-relaxed text-gray-700 dark:text-gray-300">{job.why}</p>
          </motion.div>
        )}

        {/* Skills you have */}
        {detailStep >= 3 && job.matchedSkills.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-lg border border-emerald-100/80 bg-emerald-50/40 p-2 dark:border-emerald-900/30"
          >
            <p className="mb-1 text-[8px] font-bold uppercase tracking-wide text-emerald-800 dark:text-emerald-300">
              {copy.skillsYouHave}
            </p>
            <ul className="space-y-0.5">
              {job.matchedSkills.map((s) => (
                <li key={s} className="flex items-start gap-1 text-[8px] text-gray-700 dark:text-gray-300">
                  <CheckCircle2 className="mt-0.5 h-2.5 w-2.5 shrink-0 text-emerald-600" />
                  {s}
                </li>
              ))}
            </ul>
          </motion.div>
        )}

        {/* Skill gaps */}
        {detailStep >= 4 && job.missingSkills.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-lg border border-gray-200 bg-gray-50/80 p-2 dark:border-gray-800 dark:bg-gray-900/40"
          >
            <p className="mb-1 flex items-center gap-1 text-[8px] font-bold uppercase tracking-wide text-gray-600">
              <AlertCircle className="h-2.5 w-2.5" />
              {copy.skillGaps}
            </p>
            <ul className="space-y-0.5">
              {job.missingSkills.map((s) => (
                <li key={s} className="flex items-start gap-1 text-[8px] text-gray-600 dark:text-gray-400">
                  <span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-gray-400" />
                  {s}
                </li>
              ))}
            </ul>
          </motion.div>
        )}

        {/* 6-dim breakdown */}
        {detailStep >= 5 && (
          <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} className="space-y-1">
            <p className="text-[8px] font-bold uppercase tracking-wider text-gray-400">{copy.dimScoringTitle}</p>
            {dimEntries.map(([key, val], i) => {
              const dimLabels: Record<keyof ScoreBreakdown, string> = {
                skill_overlap: copy.dimSkillOverlap,
                semantic_similarity: copy.dimSemanticFit,
                experience_alignment: copy.dimExperience,
                budget_fit: copy.dimSalaryFit,
                location_fit: copy.dimLocation,
                deadline_feasibility: copy.dimRecency,
              };
              return (
              <div key={key} className="flex items-center gap-2">
                <span className="w-[72px] shrink-0 truncate text-[7px] text-gray-500">{dimLabels[key]}</span>
                <div className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
                  <motion.div
                    className="h-full rounded-full"
                    style={{ background: scoreColor(val) }}
                    initial={{ width: 0 }}
                    animate={{ width: `${val}%` }}
                    transition={{ duration: 0.6, ease: 'easeOut', delay: i * 0.05 }}
                  />
                </div>
                <span className="w-7 shrink-0 text-right text-[7px] font-semibold" style={{ color: scoreColor(val) }}>
                  {val}%
                </span>
              </div>
              );
            })}
          </motion.div>
        )}

        {/* Next steps */}
        {detailStep >= 6 && job.actionItems.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-lg border border-gray-100 p-2 dark:border-gray-800"
          >
            <p className="mb-1.5 text-[8px] font-bold uppercase tracking-wide text-gray-400">{copy.nextStepsLabel}</p>
            <ol className="space-y-1">
              {job.actionItems.map((item, i) => (
                <li key={item} className="flex gap-1.5 text-[8px] text-gray-600 dark:text-gray-400">
                  <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-[7px] font-bold text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300">
                    {i + 1}
                  </span>
                  {item}
                </li>
              ))}
            </ol>
          </motion.div>
        )}
      </div>

      {detailStep >= 6 && (
        <div className="flex shrink-0 gap-1.5 border-t border-gray-100 p-2 dark:border-gray-800">
          <button type="button" className="hfs-agent-preview__btn hfs-agent-preview__btn--secondary flex-1">
            <Zap className="h-3 w-3" />
            {copy.btnOptimize}
          </button>
          <button type="button" className="hfs-agent-preview__btn hfs-agent-preview__btn--primary flex-1">
            {copy.btnApply}
          </button>
        </div>
      )}
    </div>
  );
}

const AutonomousMatchFeedPreview: React.FC = () => {
  const copy = useMatchFeedPreviewCopy();
  const { t } = useTranslation();
  const feedJobs = useFeedPreviewJobs();
  const firstFeedJobId = feedJobs[0]?.id ?? '';
  const pipelineSteps = useMemo(
    () => [
      copy.pipelineStep1,
      copy.pipelineStep2,
      copy.pipelineStep3,
      copy.pipelineStep4,
      copy.pipelineStep5,
    ],
    [copy],
  );
  const filterChips = useMemo(
    () => [copy.filterAllMatches, copy.filterStrongFit, copy.filterRemote, copy.filterNewToday],
    [copy],
  );
  const rootRef = useRef<HTMLDivElement>(null);
  const jobTargetRef = useRef<HTMLButtonElement>(null);

  const [phase, setPhase] = useState<Phase>('pipeline');
  const [loadProgress, setLoadProgress] = useState(0);
  const [pipelineStep, setPipelineStep] = useState(0);
  const [visibleCount, setVisibleCount] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detailStep, setDetailStep] = useState(0);
  const [cursor, setCursor] = useState({ x: 24, y: 24, visible: false, clicking: false });

  const selectedJob = feedJobs.find((j) => j.id === selectedId) ?? feedJobs[0];
  const showFeed = phase === 'feed' || phase === 'cursor-select' || phase === 'explain';
  const showExplain = phase === 'explain';
  const showGrid = showFeed && !showExplain;

  const getTargetCenter = useCallback((el: HTMLElement | null) => {
    const root = rootRef.current;
    if (!root || !el) return null;
    const rootRect = root.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    return {
      x: elRect.left - rootRect.left + elRect.width * 0.55,
      y: elRect.top - rootRect.top + elRect.height * 0.5,
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const timers: number[] = [];
    const sleep = (ms: number) =>
      new Promise<void>((res) => {
        timers.push(window.setTimeout(res, ms));
      });

    const reset = () => {
      setPhase('pipeline');
      setLoadProgress(0);
      setPipelineStep(0);
      setVisibleCount(0);
      setSelectedId(null);
      setDetailStep(0);
      setCursor({ x: 24, y: 24, visible: false, clicking: false });
    };

    const run = async () => {
      await sleep(400);
      while (!cancelled) {
        reset();
        await sleep(350);

        setPhase('pipeline');
        for (let s = 0; s < pipelineSteps.length; s++) {
          if (cancelled) return;
          setPipelineStep(s);
          for (let p = 0; p <= 100; p += 12) {
            if (cancelled) return;
            setLoadProgress(Math.min(p, 100));
            await sleep(38);
          }
          await sleep(180);
        }
        await sleep(300);

        setPhase('feed');
        for (let i = 1; i <= feedJobs.length; i++) {
          if (cancelled) return;
          setVisibleCount(i);
          await sleep(200);
        }
        await sleep(1200);

        setPhase('cursor-select');
        const start = getTargetCenter(jobTargetRef.current);
        if (start) {
          setCursor({ ...start, visible: true, clicking: false });
          await sleep(650);
          setCursor((c) => ({ ...c, clicking: true }));
          await sleep(120);
          setSelectedId(firstFeedJobId);
          setCursor((c) => ({ ...c, clicking: false, visible: false }));
        } else {
          setSelectedId(firstFeedJobId);
        }
        await sleep(350);

        setPhase('explain');
        for (let d = 1; d <= 6; d++) {
          if (cancelled) return;
          setDetailStep(d);
          await sleep(d === 5 ? 900 : 650);
        }
        await sleep(4500);
      }
    };

    void run();
    return () => {
      cancelled = true;
      timers.forEach((id) => window.clearTimeout(id));
    };
  }, [getTargetCenter, pipelineSteps.length, feedJobs.length, firstFeedJobId]);

  return (
    <div
      ref={rootRef}
      className="relative flex h-full min-h-0 flex-col overflow-hidden bg-white font-sans antialiased dark:bg-[#0f0f11]"
    >
      <PreviewCursor {...cursor} />

      <div className="flex shrink-0 items-center gap-2 border-b border-gray-100 px-3 py-2 dark:border-gray-800">
        <Sparkles className="h-3.5 w-3.5 shrink-0 text-indigo-600" />
        <span className="text-[11px] font-semibold text-gray-900 dark:text-white">{copy.title}</span>
        <span className="ml-auto flex items-center gap-1">
          <Clock className="h-3 w-3 text-emerald-500" />
          <span className="text-[9px] font-medium text-emerald-600">{copy.updatedAgo}</span>
        </span>
      </div>

      {/* Pipeline loading */}
      <AnimatePresence>
        {phase === 'pipeline' && (
          <motion.div
            key="pipeline"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="shrink-0 border-b border-gray-100 px-3 py-2 dark:border-gray-800"
          >
            <div className="mb-1 flex items-center justify-between">
              <span className="text-[9px] font-semibold text-indigo-600">
                {pipelineSteps[pipelineStep]}
              </span>
              <span className="text-[9px] text-gray-400">{loadProgress}%</span>
            </div>
            <div className="mb-2 h-1 overflow-hidden rounded-full bg-indigo-50">
              <motion.div
                className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-violet-500"
                animate={{ width: `${loadProgress}%` }}
                transition={{ duration: 0.04, ease: 'linear' }}
              />
            </div>
            <div className="flex flex-wrap gap-1">
              {pipelineSteps.map((step, i) => (
                <span
                  key={step}
                  className={`rounded-full px-1.5 py-0.5 text-[6.5px] font-medium ${
                    i < pipelineStep
                      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300'
                      : i === pipelineStep
                        ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300'
                        : 'bg-gray-100 text-gray-400 dark:bg-gray-800'
                  }`}
                >
                  {i < pipelineStep ? '✓ ' : ''}
                  {step.split(' ').slice(0, 2).join(' ')}
                </span>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Filter chips + stats */}
      {showFeed && (
        <div className="flex shrink-0 items-center gap-1.5 border-b border-gray-100 px-3 py-1.5 dark:border-gray-800">
          {filterChips.map((chip, i) => (
            <span
              key={chip}
              className={`rounded-full px-2 py-0.5 text-[7px] font-semibold ${
                i === 0
                  ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300'
                  : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
              }`}
            >
              {chip}
            </span>
          ))}
          <span className="ml-auto text-[8px] font-medium text-gray-400">
            {t('home.matchFeedPreview.rankedCount', { count: feedJobs.length, defaultValue: copy.rankedCount })}
          </span>
        </div>
      )}

      <div className="flex min-h-0 flex-1 overflow-hidden">
        {/* Feed list / grid */}
        <div
          className={`flex min-h-0 flex-col border-r border-gray-100 dark:border-gray-800 transition-all duration-300 ${
            showExplain ? 'w-[42%]' : 'w-full'
          }`}
        >
          <div className="shrink-0 border-b border-gray-100 px-3 py-1.5 dark:border-gray-800">
            <span className="text-[9px] font-bold uppercase tracking-wider text-gray-400">
              {showFeed ? copy.scoredWhileYouSleep : copy.initializingPipeline}
            </span>
          </div>

          <div className="relative min-h-0 flex-1 overflow-hidden p-2">
            {phase === 'pipeline' && (
              <div className="grid h-full grid-cols-2 grid-rows-2 gap-1.5">
                {[1, 2, 3, 4].map((n) => (
                  <div
                    key={n}
                    className="animate-pulse rounded-xl border border-gray-100 bg-gray-50/80 dark:border-gray-800 dark:bg-gray-900/30"
                  />
                ))}
              </div>
            )}

            {showGrid && (
              <div className="grid h-full min-h-0 grid-cols-2 grid-rows-2 gap-1.5">
                {feedJobs.map((job, i) => {
                  if (i >= visibleCount) {
                    return (
                      <div
                        key={job.id}
                        className="animate-pulse rounded-xl border border-gray-100 bg-gray-50/50 dark:border-gray-800 dark:bg-gray-900/20"
                      />
                    );
                  }
                  return (
                    <motion.div
                      key={job.id}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.06 }}
                      className="min-h-0"
                    >
                      <FeedJobCard
                        job={job}
                        selected={selectedId === job.id}
                        highlightRing={i === 0 && (phase === 'cursor-select' || phase === 'feed')}
                        cardRef={i === 0 ? jobTargetRef : undefined}
                        copy={copy}
                      />
                    </motion.div>
                  );
                })}
              </div>
            )}

            {showExplain && (
              <div className="space-y-1.5">
                {feedJobs.map((job) => (
                  <FeedJobCard
                    key={job.id}
                    job={job}
                    selected={selectedId === job.id}
                    compact
                    copy={copy}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Explain panel */}
        <AnimatePresence>
          {showExplain && (
            <motion.div
              key="explain"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.28, ease: 'easeOut' }}
              className="flex min-h-0 w-[58%] flex-col overflow-hidden bg-white dark:bg-[#0f0f11]"
            >
              <div className="shrink-0 border-b border-gray-100 px-3 py-1.5 dark:border-gray-800">
                <span className="text-[9px] font-bold uppercase tracking-wider text-indigo-600">
                  {copy.whyThisJob}
                </span>
              </div>
              <ExplainPanel job={selectedJob} detailStep={detailStep} copy={copy} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default AutonomousMatchFeedPreview;
